import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function DmlInLoopsPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">DML Operations in Loops</h1>
          <p className="text-lg text-gray-600 mt-2">
            How to avoid governor limit errors when updating records in Flows
          </p>
        </div>

        {/* Critical Alert */}
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-8">
          <div className="flex">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Critical Performance Issue</h3>
              <p className="mt-2 text-sm text-red-700">
                DML operations (Create, Update, Delete) inside loops will cause your flow to fail
                when processing large batches of records. Salesforce allows only 150 DML statements per transaction.
              </p>
            </div>
          </div>
        </div>

        {/* What is it? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are DML Operations in Loops?</h2>
          <p className="text-gray-700 mb-4">
            A "DML in Loop" occurs when you place a Create Records, Update Records, or Delete Records
            element inside a Loop element. Each iteration performs a separate database operation:
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>Processing 10 records = 10 DML statements</li>
            <li>Processing 50 records = 50 DML statements</li>
            <li>Processing 200 records = 200 DML statements (exceeds 150 DML limit)</li>
          </ul>

          <div className="mt-4 p-4 bg-gray-50 rounded">
            <p className="text-sm text-gray-600">
              <strong>DML stands for:</strong> Data Manipulation Language - operations that modify data in Salesforce
              (Create, Update, Delete, Upsert)
            </p>
          </div>
        </div>

        {/* Why is it bad? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Why Is This a Problem?</h2>

          <div className="space-y-4">
            <div className="border-l-4 border-red-500 pl-4">
              <h3 className="font-semibold text-gray-900">Governor Limit Violations</h3>
              <p className="text-gray-700 mt-1">
                Salesforce allows only 150 DML statements per transaction. If your flow processes
                150+ trigger records, it will fail with "Too many DML statements: 151" error.
              </p>
            </div>

            <div className="border-l-4 border-orange-500 pl-4">
              <h3 className="font-semibold text-gray-900">All-or-Nothing Failures</h3>
              <p className="text-gray-700 mt-1">
                When a DML limit is hit, the entire transaction rolls back. None of your updates
                are saved, even the ones that completed before the error.
              </p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <h3 className="font-semibold text-gray-900">Performance Degradation</h3>
              <p className="text-gray-700 mt-1">
                Each DML operation locks database rows and triggers additional automation.
                Hundreds of separate DML calls can take several seconds to complete.
              </p>
            </div>
          </div>
        </div>

        {/* How to identify */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify This Issue</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check your Performance Scorecard on the flow detail page</li>
                <li>Look for "DML Operations in Loops" in the Issues Detected section</li>
                <li>Review the Recommended Fixes tab for specific Update/Create/Delete elements</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In Salesforce Flow Builder:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Look for Create/Update/Delete Records elements connected after a Loop element</li>
                <li>Check if these elements are updating the current loop item</li>
                <li>Example: Loop through Contacts, Update Current Contact inside loop</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Common Patterns That Cause This:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Loop through records → Update each record individually</li>
                <li>Loop through records → Create related records one at a time</li>
                <li>Loop through records → Delete matching records individually</li>
              </ul>
            </div>
          </div>
        </div>

        {/* The Fix */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Fix It</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Solution</h3>
                <p className="mt-2 text-sm text-green-700">
                  Collect records in a collection variable inside the loop, then perform ONE DML operation
                  after the loop completes.
                </p>
              </div>
            </div>
          </div>

          <h3 className="font-semibold text-gray-900 mb-3">Step-by-Step Fix:</h3>

          <ol className="space-y-6">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Create a Collection Variable</h4>
                  <p className="text-gray-700 mt-1">
                    Before the loop, create a new variable to hold all records that need to be updated.
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Variable: recordsToUpdate (Collection of [Object Type])
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Add to Collection Inside Loop</h4>
                  <p className="text-gray-700 mt-1">
                    Replace the Update/Create/Delete element with an Assignment that adds the record to your collection.
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Assignment: Add &#123;!CurrentItem&#125; to &#123;!recordsToUpdate&#125;
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Perform DML After Loop</h4>
                  <p className="text-gray-700 mt-1">
                    After the loop completes, add ONE Update/Create/Delete Records element that processes
                    the entire collection.
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Update Records: &#123;!recordsToUpdate&#125;
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Remove DML from Loop</h4>
                  <p className="text-gray-700 mt-1">
                    Delete the Update/Create/Delete element that was inside the loop.
                  </p>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (Bad)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">// Loop through Contacts</div>
                <div className="pl-4">Loop: ContactCollection</div>
                <div className="pl-8">Decision: Check if Email blank</div>
                <div className="pl-12 text-red-600">Update Records: CurrentContact</div>
                <div className="pl-12 text-red-600">Set Email = "noemail@domain.com"</div>
              </div>
              <div className="mt-3 text-sm text-red-700">
                Result: 200 contacts = 200 DML = FAILURE
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (Good)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-gray-600">// Loop through Contacts</div>
                <div className="pl-4">Loop: ContactCollection</div>
                <div className="pl-8">Decision: Check if Email blank</div>
                <div className="pl-12 text-green-600">Set CurrentContact.Email = ...</div>
                <div className="pl-12 text-green-600">Add to contactsToUpdate</div>
                <div className="mt-2 text-green-600">Update Records: contactsToUpdate</div>
              </div>
              <div className="mt-3 text-sm text-green-700">
                Result: 200 contacts = 1 DML = SUCCESS
              </div>
            </div>
          </div>
        </div>

        {/* Special Cases */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Special Cases</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900">Creating Related Records</h3>
              <p className="text-gray-700 mt-2">
                If you're creating child records (like creating a Contact for each Account):
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2">
                <li>Build all child records in a collection inside the loop</li>
                <li>Use Assignment to set parent ID references</li>
                <li>Create all child records in one operation after the loop</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">Conditional Updates</h3>
              <p className="text-gray-700 mt-2">
                If you only update some records based on a condition:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2">
                <li>Use a Decision element inside the loop</li>
                <li>Only add records to the collection if they meet your criteria</li>
                <li>Update the filtered collection after the loop</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900">Mixed Operations</h3>
              <p className="text-gray-700 mt-2">
                If you need to create some records and update others:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2">
                <li>Use separate collections for creates vs updates</li>
                <li>After the loop, perform separate DML for each collection</li>
                <li>This still results in only 2 DML operations total</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Improvement</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">DML Statement Usage: 99% reduction</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Before: Up to 200 DML statements (exceeds limit)</li>
              <li>After: 1-2 DML statements (well within limit)</li>
            </ul>
            <p className="mt-4">
              This fix allows your flow to process 200+ records in a single execution without
              hitting governor limits.
            </p>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/queries-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                Queries in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Similar pattern with database queries</p>
            </li>
            <li>
              <Link to={`${basePath}/help/bulkification`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Bulkification →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Processing multiple records efficiently</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to high-performance flows</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
